if (!customElements.get('bso-blog-search')) {
  const RESULTS_SECTION = 'bso-blog-search-results';
  const MIN_CHARS = 2;

  const escapeRegExp = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  class BsoBlogSearch extends HTMLElement {
    connectedCallback() {
      if (this.initialized || !this.hasAttribute('data-predictive')) return;
      this.initialized = true;

      this.form = this.querySelector('form');
      this.input = this.querySelector('.js-bso-search-input');
      this.results = this.querySelector('.js-bso-search-results');
      this.resetBtn = this.querySelector('.js-bso-search-reset');
      this.statusEl = this.querySelector('.js-bso-search-status');

      this.url = this.dataset.url || '/search/suggest';
      this.blog = this.dataset.blog || '';
      this.limit = parseInt(this.dataset.limit, 10) || 6;
      this.fields = this.dataset.fields || 'title,tag,author,body';
      this.types = this.dataset.types || 'article,query';
      this.stayOnPage = this.hasAttribute('data-stay-on-page');
      this.cache = new Map();

      this.onInput = this.debounce(this.handleInput.bind(this), 250);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onDocPointerDown = this.handleDocPointerDown.bind(this);
      this.onResize = this.debounce(this.updatePosition.bind(this), 100);

      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('focusout', this.handleFocusOut.bind(this));
      this.form.addEventListener('submit', this.handleSubmit.bind(this));
      this.resetBtn.addEventListener('click', this.handleReset.bind(this));
      this.results.addEventListener('click', this.handleResultsClick.bind(this));
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      document.removeEventListener('pointerdown', this.onDocPointerDown);
      window.removeEventListener('resize', this.onResize);
    }

    debounce(fn, wait) {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn(...args), wait);
      };
    }

    getQuery() {
      return this.input.value.trim();
    }

    handleInput() {
      const query = this.getQuery();
      this.resetBtn.hidden = query.length === 0;

      if (query.length < MIN_CHARS) {
        this.close();
        return;
      }
      this.getResults(query);
    }

    handleFocus() {
      const query = this.getQuery();
      if (query.length < MIN_CHARS) return;
      this.showResults(query);
    }

    /**
     * Opens the already rendered results for a query, or fetches them.
     * @param {string} query - Search query.
     */
    showResults(query) {
      if (this.renderedQuery === query && this.results.innerHTML) {
        this.open();
      } else {
        this.getResults(query);
      }
    }

    handleSubmit(evt) {
      const query = this.getQuery();
      if (!query.length) {
        evt.preventDefault();
        return;
      }

      if (!this.stayOnPage) return;

      evt.preventDefault();
      this.resetBtn.hidden = false;
      this.showResults(query);
      // Hide the on-screen keyboard so the results are visible on touch devices.
      if (window.matchMedia('(pointer: coarse)').matches) this.input.blur();
    }

    /**
     * "View all" always opens the search page; in stay-on-page mode a suggestion
     * fills the input and searches in place.
     * @param {object} evt - Event object.
     */
    handleResultsClick(evt) {
      // type="button" (not submit), so Enter in the input never picks it as the default
      // button. form.submit() skips the submit event, so stay-on-page doesn't block it.
      if (evt.target.closest('.js-bso-search-view-all')) {
        if (this.getQuery().length) this.form.submit();
        return;
      }

      if (!this.stayOnPage) return;
      const suggestion = evt.target.closest('.bso-blog-search__query');
      if (!suggestion) return;

      evt.preventDefault();
      this.input.value = suggestion.textContent.trim();
      this.input.focus();
      this.resetBtn.hidden = false;
      this.getResults(this.getQuery());
    }

    handleReset() {
      this.input.value = '';
      this.resetBtn.hidden = true;
      this.close();
      this.input.focus();
    }

    handleDocPointerDown(evt) {
      if (!this.contains(evt.target)) this.close();
    }

    handleFocusOut(evt) {
      // Only close on keyboard focus leaving (relatedTarget is null for many mouse clicks).
      if (evt.relatedTarget && !this.contains(evt.relatedTarget)) this.close();
    }

    handleKeydown(evt) {
      switch (evt.key) {
        case 'ArrowDown':
        case 'ArrowUp':
          if (!this.hasAttribute('open')) return;
          evt.preventDefault();
          this.moveSelection(evt.key === 'ArrowDown' ? 1 : -1);
          break;

        case 'Enter': {
          const selected = this.getSelected();
          if (!selected) return;
          evt.preventDefault();
          const target = selected.matches('a, button') ? selected : selected.querySelector('a, button');
          if (target) target.click();
          break;
        }

        case 'Escape':
          if (this.hasAttribute('open')) {
            evt.preventDefault();
            this.close();
          }
          break;

        // no default
      }
    }

    getOptions() {
      // Skip options hidden via [hidden] or CSS toggles (e.g. view-all disabled).
      return [...this.results.querySelectorAll('[role="option"]')].filter(
        (el) => el.getClientRects().length > 0
      );
    }

    getSelected() {
      return this.results.querySelector('[role="option"][aria-selected="true"]');
    }

    moveSelection(step) {
      const options = this.getOptions();
      if (!options.length) return;

      const current = this.getSelected();
      let index = options.indexOf(current) + step;
      if (!current && step < 0) index = options.length - 1;
      if (index < 0) index = options.length - 1;
      if (index >= options.length) index = 0;

      if (current) current.setAttribute('aria-selected', 'false');
      const next = options[index];
      next.setAttribute('aria-selected', 'true');
      next.scrollIntoView({ block: 'nearest' });
      this.input.setAttribute('aria-activedescendant', next.id);
    }

    async getResults(query) {
      const key = query.toLowerCase();
      if (this.cache.has(key)) {
        this.renderResults(this.cache.get(key), query);
        return;
      }

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      const params = new URLSearchParams();
      params.set('q', query);
      params.set('resources[type]', this.types);
      // Predictive search can't filter by blog, so fetch the max and filter client-side.
      params.set('resources[limit]', this.blog ? 10 : this.limit);
      params.set('resources[limit_scope]', 'each');
      params.set('resources[options][fields]', this.fields);
      params.set('resources[options][prefix]', 'last');
      params.set('section_id', RESULTS_SECTION);

      this.setAttribute('loading', '');
      this.setStatus(this.statusEl.dataset.loadingText);

      try {
        const response = await fetch(`${this.url}?${params.toString()}`, {
          signal: this.controller.signal
        });
        if (!response.ok) throw new Error(response.status);

        const html = (await response.text()).replace(/psearch/g, this.input.id);
        const markup = this.processResults(html, query);
        this.cache.set(key, markup);

        // Ignore responses for a query the user has already moved past.
        if (this.getQuery().toLowerCase() === key) this.renderResults(markup, query);
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.close();
          console.error('[bso-blog-search]', error); // eslint-disable-line no-console
        }
      } finally {
        this.removeAttribute('loading');
      }
    }

    /**
     * Applies blog scope, article limit and term highlighting to the fetched section markup.
     * @param {string} html - Section Rendering API response.
     * @param {string} query - Search query.
     * @returns {string}
     */
    processResults(html, query) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;
      const root = tmpl.content.querySelector('.bso-blog-search__results');
      if (!root) return '';

      const articleGroup = root.querySelector('.bso-blog-search__group--articles');
      if (articleGroup) {
        const items = [...articleGroup.querySelectorAll('[data-blog]')];
        const kept = items.filter((item) => !this.blog || item.dataset.blog === this.blog);
        items.forEach((item) => {
          if (!kept.includes(item) || kept.indexOf(item) >= this.limit) item.remove();
        });
        if (!articleGroup.querySelector('[data-blog]')) articleGroup.remove();
      }

      const hasResults = root.querySelector('.bso-blog-search__group');
      root.querySelector('.bso-blog-search__no-results').hidden = !!hasResults;
      root.querySelector('.bso-blog-search__footer').hidden = !hasResults;
      if (!hasResults) root.classList.add('bso-blog-search__results--empty');

      this.highlight(root, query);
      return root.outerHTML;
    }

    highlight(root, query) {
      const words = query.split(/\s+/).filter((w) => w.length > 1).map(escapeRegExp);
      if (!words.length) return;
      const pattern = new RegExp(`(${words.join('|')})`, 'gi');

      root.querySelectorAll('.js-highlight').forEach((el) => {
        const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
        const nodes = [];
        while (walker.nextNode()) nodes.push(walker.currentNode);

        nodes.forEach((node) => {
          const parts = node.nodeValue.split(pattern);
          if (parts.length < 2) return;
          const frag = document.createDocumentFragment();
          // split() with a capture group puts matches at odd indexes
          parts.forEach((part, i) => {
            if (!part) return;
            if (i % 2) {
              const mark = document.createElement('mark');
              mark.textContent = part;
              frag.append(mark);
            } else {
              frag.append(part);
            }
          });
          node.replaceWith(frag);
        });
      });
    }

    renderResults(markup, query) {
      if (!markup) {
        this.close();
        return;
      }
      this.results.innerHTML = markup;
      this.renderedQuery = query;
      this.input.removeAttribute('aria-activedescendant');

      const count = this.results.querySelectorAll('[data-blog]').length;
      const noResults = this.results.querySelector('.bso-blog-search__no-results');
      this.setStatus(noResults && !noResults.hidden ? noResults.textContent.trim() : String(count));
      this.open();
    }

    setStatus(text) {
      if (this.statusEl) this.statusEl.textContent = text || '';
    }

    /**
     * Sizes the dropdown to the full viewport width below the field (applied by CSS on mobile only).
     */
    updatePosition() {
      const field = this.results.parentElement.getBoundingClientRect();
      const viewportWidth = document.documentElement.clientWidth;
      this.results.style.setProperty('--bso-bs-dd-left', `${-field.left}px`);
      this.results.style.setProperty('--bso-bs-dd-width', `${viewportWidth}px`);
      this.results.style.setProperty(
        '--bso-bs-dd-max-height',
        `${Math.max(window.innerHeight - field.bottom - 8, 240)}px`
      );
    }

    open() {
      this.updatePosition();
      this.results.hidden = false;
      this.setAttribute('open', '');
      this.input.setAttribute('aria-expanded', 'true');
      document.addEventListener('pointerdown', this.onDocPointerDown);
      window.addEventListener('resize', this.onResize);
    }

    close() {
      const selected = this.getSelected();
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.results.hidden = true;
      this.removeAttribute('open');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      document.removeEventListener('pointerdown', this.onDocPointerDown);
      window.removeEventListener('resize', this.onResize);
    }
  }

  customElements.define('bso-blog-search', BsoBlogSearch);
}
